var PmdComponents = (function (exports) {
    'use strict';

    class PMDProduct extends HTMLElement {
        static get observedAttributes() {
            return ['loading', 'price-format', 'selected-variant', 'first-available', 'form-id', 'url-sync'];
        }

        constructor() {
            super();
            this.productData = null;
            this.isLoading = false;
            this.currentOptions = {};
            this.selectedVariant = null;
        }

        connectedCallback() {
            requestAnimationFrame(() => {
                this._init();
                this.addEventListener('pmd:product-option:change', this._onOptionChange.bind(this));
            });
        }

        _onOptionChange(e) {
            if (this._isSyncing) return;
            const { name, value } = e.detail;
            this.currentOptions[name] = value;
            this._updateSelectedVariant();
        }

        _getOptionName(option) {
            return typeof option === 'string' ? option : option.name;
        }

        _updateSelectedVariant() {
            if (!this.productData || !this.productData.variants) return;

            const selectedVariant = this.productData.variants.find(variant => {
                return this.productData.options.every((option, index) => {
                    const variantOptionValue = variant[`option${index + 1}`];
                    return this.currentOptions[this._getOptionName(option)] === variantOptionValue;
                });
            });

            this.selectedVariant = selectedVariant || null;

            this._isSyncing = true;
            if (selectedVariant && selectedVariant.id) {
                this.setAttribute('selected-variant', selectedVariant.id);
            } else {
                this.removeAttribute('selected-variant');
            }
            this._isSyncing = false;

            const formId = this.getAttribute('form-id');
            if (formId) {
                const form = document.getElementById(formId);
                if (form) {
                    const idInput = form.querySelector('input[name="id"]');
                    if (idInput) {
                        idInput.value = selectedVariant && selectedVariant.id ? selectedVariant.id : '';
                    }
                }
            }

            if (this.hasAttribute('url-sync')) {
                try {
                    const url = new URL(window.location.href);
                    if (selectedVariant && selectedVariant.id) {
                        url.searchParams.set('variant', selectedVariant.id);
                    } else {
                        url.searchParams.delete('variant');
                    }
                    window.history.replaceState(window.history.state, '', url);
                } catch (error) {
                    // replaceState can throw on file:// or sandboxed pages; selection still works
                }
            }

            this.dispatchEvent(new CustomEvent('pmd:product:variant-change', {
                bubbles: true,
                composed: true,
                detail: {
                    productId: this.id || null,
                    product: this.productData,
                    variant: this.selectedVariant,
                    options: this.currentOptions
                }
            }));
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (oldValue === newValue) return;
            if (name === 'price-format' && this.isConnected) {
                this._updateSelectedVariant();
            }
            if (name === 'first-available' && this.isConnected) {
                this._onProductLoaded(); // Re-evaluate availability if needed
            }
        }

        _syncOptionsToChildren() {
            const optionComponents = this.querySelectorAll('pmd-product-option');
            optionComponents.forEach(comp => {
                const name = comp.getAttribute('name');
                const value = this.currentOptions[name];
                if (value !== undefined) {
                    const escapedValue = window.CSS && CSS.escape ? CSS.escape(value) : value;
                    const input = comp.querySelector(`input[value="${escapedValue}"], select`);
                    if (input) {
                        if (input.tagName === 'SELECT') {
                            input.value = value;
                        } else if (input.type === 'radio' || input.type === 'checkbox') {
                            const radio = comp.querySelector(`input[value="${escapedValue}"]`);
                            if (radio) radio.checked = true;
                        }
                        // Trigger a change event if needed, but pmd-product-option listens for changes on these.
                        // Actually, if we change the value programmatically, we might need to notify the pmd-product-option
                        // to update its internal state if it has any, but currently it just bubbles events.
                        // However, we are already in pmd-product, so we just need to make sure the UI matches.
                    }
                }
            });
        }

        async _init() {
            this._loadFromScript();
        }


        _loadFromScript() {
            const script = this.querySelector('script[type="application/json"]');
            if (script) {
                try {
                    this.productData = JSON.parse(script.textContent);
                    this._onProductLoaded();
                } catch (error) {
                    console.error('PMDProduct: Error parsing script JSON', error);
                }
            }
        }

        _setLoading(loading) {
            this.isLoading = loading;
            if (loading) {
                this.setAttribute('loading', '');
            } else {
                this.removeAttribute('loading');
            }
            this.dispatchEvent(new CustomEvent('pmd:product:loading', {
                bubbles: true,
                composed: true,
                detail: { loading }
            }));
        }

        _onProductLoaded() {
            if (!this.productData) return;

            // Collect current options from children if they exist, otherwise use initial variant
            const optionComponents = this.querySelectorAll('pmd-product-option');
            if (optionComponents.length > 0) {
                optionComponents.forEach(comp => {
                    const name = comp.getAttribute('name');
                    const checkedInput = comp.querySelector('input:checked') || comp.querySelector('select');
                    if (checkedInput) {
                        this.currentOptions[name] = checkedInput.value;
                    }
                });
            }

            let selectedVariant = this._findVariantByOptions(this.currentOptions);
            const firstAvailableAttr = this.getAttribute('first-available');
            const firstAvailable = firstAvailableAttr === null || firstAvailableAttr === 'true';

            // A ?variant= deep link takes precedence over pre-checked inputs and
            // first-available switching (Shopify honors deep links even when sold out)
            let urlVariantApplied = false;
            if (this.hasAttribute('url-sync')) {
                const urlVariantId = new URLSearchParams(window.location.search).get('variant');
                if (urlVariantId) {
                    const urlVariant = this.productData.variants.find(v => String(v.id) === urlVariantId);
                    if (urlVariant) {
                        selectedVariant = urlVariant;
                        urlVariantApplied = true;
                        this.productData.options.forEach((option, index) => {
                            this.currentOptions[this._getOptionName(option)] = urlVariant[`option${index + 1}`];
                        });
                        this._syncOptionsToChildren();
                    }
                }
            }

            // If not available or not found, and first-available is true, find first available
            if (!urlVariantApplied && (!selectedVariant || !selectedVariant.available) && firstAvailable) {
                const availableVariant = this.productData.variants.find(v => v.available);
                if (availableVariant) {
                    selectedVariant = availableVariant;
                    // Update currentOptions to match this variant
                    this.productData.options.forEach((option, index) => {
                        this.currentOptions[this._getOptionName(option)] = selectedVariant[`option${index + 1}`];
                    });
                    // Sync UI to match the new selection
                    this._syncOptionsToChildren();
                }
            }

            // If still no variant, fallback to first variant
            if (!selectedVariant && this.productData.variants.length > 0) {
                selectedVariant = this.productData.variants[0];
                this.productData.options.forEach((option, index) => {
                    this.currentOptions[this._getOptionName(option)] = selectedVariant[`option${index + 1}`];
                });
                this._syncOptionsToChildren();
            }

            this.dispatchEvent(new CustomEvent('pmd:product:loaded', {
                bubbles: true,
                composed: true,
                detail: {
                    product: this.productData
                }
            }));

            this._updateSelectedVariant();
        }

        _findVariantByOptions(options) {
            if (!this.productData || !this.productData.variants) return null;
            return this.productData.variants.find(variant => {
                return this.productData.options.every((option, index) => {
                    const variantOptionValue = variant[`option${index + 1}`];
                    return options[this._getOptionName(option)] === variantOptionValue;
                });
            });
        }
    }

    class PMDProductOption extends HTMLElement {
        static get observedAttributes() {
            return ['name'];
        }

        connectedCallback() {
            requestAnimationFrame(() => {
                this._init();
            });
        }

        _init() {
            this.addEventListener('change', (e) => {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') {
                    this._onInputChange();
                }
            });
            
            // Initial state
            this._onInputChange();
        }

        _onInputChange() {
            const input = this.querySelector('input, select');
            if (!input) return;

            let value;
            if (input.tagName === 'SELECT') {
                value = input.value;
            } else if (input.type === 'radio' || input.type === 'checkbox') {
                const checkedInput = this.querySelector('input:checked');
                value = checkedInput ? checkedInput.value : null;
            } else {
                value = input.value;
            }

            if (value === null || value === undefined) return;

            this.currentValue = value;

            this.dispatchEvent(new CustomEvent('pmd:product-option:change', {
                bubbles: true,
                composed: true,
                detail: {
                    name: this.getAttribute('name'),
                    value: value
                }
            }));
        }
    }

    class PMDOptionValue extends HTMLElement {
        connectedCallback() {
            // Listen where the event actually bubbles: the containing option when
            // nested inside one, otherwise the product (or document) scope.
            this._scope = this.closest('pmd-product-option') || this.closest('pmd-product') || document;
            this._onOptionChangeBound = this._onOptionChange.bind(this);
            this._scope.addEventListener('pmd:product-option:change', this._onOptionChangeBound);

            // Initial state might have already been set if the option initialized before this.
            requestAnimationFrame(() => {
                const source = this._getSourceOption();
                if (source && source.currentValue) {
                    this._updateValue(source.currentValue);
                }
            });
        }

        disconnectedCallback() {
            if (this._scope) {
                this._scope.removeEventListener('pmd:product-option:change', this._onOptionChangeBound);
            }
        }

        _getOptionName() {
            const own = this.getAttribute('name');
            if (own) return own;
            const parent = this.closest('pmd-product-option');
            return parent ? parent.getAttribute('name') : null;
        }

        _getSourceOption() {
            const parent = this.closest('pmd-product-option');
            if (parent) return parent;
            const name = this.getAttribute('name');
            if (!name) return null;
            const root = this.closest('pmd-product') || document;
            const escapedName = window.CSS && CSS.escape ? CSS.escape(name) : name;
            return root.querySelector(`pmd-product-option[name="${escapedName}"]`);
        }

        _onOptionChange(e) {
            const { name, value } = e.detail;
            const myName = this._getOptionName();

            if (myName && myName !== name) return;

            this._updateValue(value);
        }

        _updateValue(value) {
            if (value === undefined || value === null) return;
            const target = this.querySelector('[data-option-value-target]') || this;
            target.textContent = value;
        }
    }

    class PMDVariantTitle extends HTMLElement {
        connectedCallback() {
            const parentProduct = this.closest('pmd-product');
            if (parentProduct) {
                parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            }
            requestAnimationFrame(() => {
                const parentProduct = this.closest('pmd-product');
                if (parentProduct && parentProduct.selectedVariant) {
                    this._onVariantChange({
                        detail: {
                            variant: parentProduct.selectedVariant,
                            options: parentProduct.currentOptions
                        }
                    });
                }
            });
        }

        _onVariantChange(e) {
            const variant = e.detail.variant;
            const target = this.querySelector('[data-variant-title-target]') || this;
            
            if (variant && variant.title) {
                target.textContent = variant.title;
            } else if (!variant) {
                // If no variant is found for selected options, clear the title or show a placeholder
                target.textContent = '';
            }
        }
    }

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
        if (isNaN(number) || number == null) return '';

        number = (number / 100).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
    }

    function stripTrailingZeros(value, decimal) {
        const suffix = decimal + '00';
        return value.endsWith(suffix) ? value.slice(0, -suffix.length) : value;
    }

    /**
     * Find the `pmd-product` a component belongs to.
     *
     * Normally that is the nearest ancestor. A component that cannot be nested -
     * a themed add-to-cart button that has to live in the page's own button row,
     * say - can instead point at the host by id with `for="<host id>"`, the same
     * way `pmd-subscription-box[form]` points at a form.
     *
     * An unresolvable `for` returns null rather than falling back to `closest()`:
     * an explicit reference that misses is a mistake, and silently binding to some
     * other product would be worse than doing nothing.
     */
    function resolveProductHost(element) {
        if (!element || typeof element.getAttribute !== 'function') return null;

        const ref = element.getAttribute('for');
        if (ref) {
            const host = document.getElementById(ref);
            return host && host.tagName === 'PMD-PRODUCT' ? host : null;
        }

        return element.closest('pmd-product');
    }

    function getPriceFormat(element) {
        let format = window.PMDPriceFormat || '${{amount}}';

        const parentProduct = resolveProductHost(element);
        if (parentProduct && parentProduct.hasAttribute('price-format')) {
            format = parentProduct.getAttribute('price-format');
        }

        // An element-level override wins over the product's format, so one price in
        // a buy box can render differently from the rest - e.g. an add-to-cart label
        // that drops trailing zeros while the subscription rows keep their cents.
        if (element && typeof element.hasAttribute === 'function' && element.hasAttribute('price-format')) {
            format = element.getAttribute('price-format');
        }

        return format;
    }

    function formatPrice(price, format) {
        if (typeof price === 'string') {
            price = price.replace('.', '');
        }
        let value = '';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholderRegex);

        if (!match) return price;

        const amountType = match[1];

        switch (amountType) {
            case 'amount':
                value = formatWithDelimiters(price, 2);
                break;
            case 'amount_no_decimals':
                value = formatWithDelimiters(price, 0);
                break;
            case 'amount_with_comma_separator':
                value = formatWithDelimiters(price, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                value = formatWithDelimiters(price, 0, '.', ',');
                break;
            // Shopify's `money_without_trailing_zeros`: cents only when there are
            // any, so $108.00 renders as $108 but $97.20 keeps its cents.
            case 'amount_no_trailing_zeros':
                value = stripTrailingZeros(formatWithDelimiters(price, 2), '.');
                break;
            case 'amount_no_trailing_zeros_with_comma_separator':
                value = stripTrailingZeros(formatWithDelimiters(price, 2, '.', ','), ',');
                break;
            default:
                value = formatWithDelimiters(price, 2);
        }

        return format.replace(placeholderRegex, value);
    }

    class PMDPriceBase extends HTMLElement {
        _onVariantChange(e) {
            // To be implemented by subclasses
        }

        _initFromParent() {
            const parentProduct = this.closest('pmd-product');
            if (parentProduct && parentProduct.selectedVariant) {
                this._onVariantChange({
                    detail: {
                        variant: parentProduct.selectedVariant,
                        options: parentProduct.currentOptions
                    }
                });
            }
        }

        _formatPrice(price) {
            return formatPrice(price, getPriceFormat(this));
        }
    }

    class PMDPriceRegular extends PMDPriceBase {
        connectedCallback() {
            const parentProduct = this.closest('pmd-product');
            if (parentProduct) {
                parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            }
            requestAnimationFrame(() => this._initFromParent());
        }

        _onVariantChange(e) {
            const variant = e.detail.variant;
            if (variant && variant.price !== undefined) {
                const target = this.querySelector('[data-price-target]') || this;
                target.textContent = this._formatPrice(variant.price);
            }
        }
    }

    class PMDPriceCompareAt extends PMDPriceBase {
        connectedCallback() {
            const parentProduct = this.closest('pmd-product');
            if (parentProduct) {
                parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            }
            requestAnimationFrame(() => this._initFromParent());
        }

        _onVariantChange(e) {
            const variant = e.detail.variant;
            if (variant && variant.compare_at_price !== undefined) {
                const target = this.querySelector('[data-price-target]') || this;
                if (variant.compare_at_price > variant.price) {
                    target.textContent = this._formatPrice(variant.compare_at_price);
                    this.removeAttribute('hidden');
                } else {
                    target.textContent = '';
                    this.setAttribute('hidden', '');
                }
            } else {
                this.setAttribute('hidden', '');
            }
        }
    }

    class PMDATCButton extends HTMLElement {
        static get observedAttributes() {
            return ['available'];
        }

        connectedCallback() {
            const parentProduct = resolveProductHost(this);
            if (parentProduct) {
                parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            }
            requestAnimationFrame(() => {
                if (parentProduct && parentProduct.selectedVariant) {
                    this._onVariantChange({
                        detail: {
                            variant: parentProduct.selectedVariant,
                            options: parentProduct.currentOptions
                        }
                    });
                }
            });
        }

        _onVariantChange(e) {
            const variant = e.detail.variant;
            if (variant) {
                this.setAttribute('available', variant.available ? 'true' : 'false');
                this._updateInternalButton(variant.available);
            } else {
                this.setAttribute('available', 'false');
                this._updateInternalButton(false);
            }
        }

        _updateInternalButton(available) {
            const button = this.querySelector('button');
            if (!button) return;

            // A button the page has deliberately locked - a gift card that needs its
            // recipient form filled in first, say - stays disabled. Availability is
            // only one of the reasons a button can be off, so it must not re-enable
            // one that was disabled for some other reason.
            if (button.hasAttribute('data-pmd-locked')) return;

            button.disabled = !available;
        }
    }

    class PMDSubscriptionBox extends HTMLElement {
        static get observedAttributes() {
            return ['form'];
        }

        constructor() {
            super();
            this.selectedType = 'one-time'; // 'one-time' or 'subscription'
        }

        connectedCallback() {
            const parentProduct = this.closest('pmd-product');
            if (parentProduct) {
                parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            }

            this.addEventListener('change', this._onInternalChange.bind(this));

            requestAnimationFrame(() => {
                const checkedRadio = this.querySelector('input[type="radio"]:checked');
                if (checkedRadio) {
                    this.selectedType = checkedRadio.value;
                }

                if (parentProduct && parentProduct.selectedVariant) {
                    this._onVariantChange({ detail: { variant: parentProduct.selectedVariant } });
                }
                this._propagateFormAttr();
            });
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (name === 'form') {
                this._propagateFormAttr();
            }
        }

        _onVariantChange(e) {
            const variant = e.detail.variant;
            if (!variant) return;

            // Check if variant has selling plan allocations
            const hasSubscriptions = variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0;
            
            if (!hasSubscriptions) {
                this.selectedType = 'one-time';
                this.setAttribute('one-time-only', '');
                this._updateInputs();
                // Maybe hide subscription group if no plans available?
                // For now just ensure one-time is selected if subscription was active but no longer available
            } else {
                this.removeAttribute('one-time-only');
            }
            
            this.dispatchEvent(new CustomEvent('pmd:subscription:variant-change', {
                bubbles: true,
                composed: true,
                detail: { variant }
            }));
        }

        _onInternalChange(e) {
            if (e.target.type === 'radio') {
                this.selectedType = e.target.value;
                this._updateInputs();
                this._propagateFormAttr();
                
                this.dispatchEvent(new CustomEvent('pmd:subscription:type-change', {
                    bubbles: true,
                    composed: true,
                    detail: { type: this.selectedType }
                }));
            }
        }

        _propagateFormAttr() {
            const formId = this.getAttribute('form');
            const subscriptionGroup = this.querySelector('pmd-subscription-group');
            if (!subscriptionGroup) return;

            const select = subscriptionGroup.querySelector('select');
            if (!select) return;

            if (this.selectedType === 'subscription' && formId) {
                select.setAttribute('form', formId);
            } else {
                select.setAttribute('form', 'none');
            }
        }

        _updateInputs() {
            const inputs = this.querySelectorAll('input[type="radio"]');
            inputs.forEach(input => {
                input.checked = (input.value === this.selectedType);
            });
        }
    }

    class PMDOneTimePurchase extends HTMLElement {
        connectedCallback() {
            this.parentElement.addEventListener('pmd:subscription:variant-change', this._onVariantChange.bind(this));
            
            requestAnimationFrame(() => {
                const parent = this.closest('pmd-subscription-box');
                if (parent) {
                    const radio = this.querySelector('input[type="radio"]');
                    if (radio) {
                        // Add form attribute if parent has it
                        const formId = parent.getAttribute('form');
                        if (formId) {
                            radio.setAttribute('form', formId);
                        }
                    }
                }
            });
        }

        _onVariantChange(e) {
            e.detail.variant;
            // One-time purchase is usually always available unless the product requires a selling plan
            // But for simplicity, we just keep it visible
            // Some Shopify products might have `requires_selling_plan: true`
        }
    }

    class PMDSubscriptionGroup extends HTMLElement {
        connectedCallback() {
            const subscriptionBox = this.closest('pmd-subscription-box');
            if (subscriptionBox) {
                subscriptionBox.addEventListener('pmd:subscription:variant-change', this._onVariantChange.bind(this));
            }
            
            requestAnimationFrame(() => {
                const parent = this.closest('pmd-subscription-box');
                if (parent) {
                    const radio = this.querySelector('input[type="radio"]');
                    if (radio) {
                        // Add form attribute if parent has it
                        const formId = parent.getAttribute('form');
                        if (formId) {
                            radio.setAttribute('form', formId);
                        }
                    }
                }
            });
        }

        _onVariantChange(e) {
            const variant = e.detail.variant;
            const parentProduct = this.closest('pmd-product');
            if (!variant || !parentProduct) return;

            const select = this.querySelector('select');
            if (!select) return;

            // Clear existing options
            select.innerHTML = '';

            // If no allocations, hide this group maybe?
            if (!variant.selling_plan_allocations || variant.selling_plan_allocations.length === 0) {
                this.setAttribute('hidden', '');
                return;
            }

            this.removeAttribute('hidden');

            // Find selling plan details from productData
            const productData = parentProduct.productData;
            if (!productData || !productData.selling_plan_groups) return;

            variant.selling_plan_allocations.forEach((allocation, index) => {
                // Find the selling plan in the productData
                let sellingPlan = null;
                productData.selling_plan_groups.forEach(group => {
                    const found = group.selling_plans.find(p => p.id === allocation.selling_plan_id);
                    if (found) sellingPlan = found;
                });

                if (sellingPlan) {
                    const option = document.createElement('option');
                    option.value = sellingPlan.id;
                    option.textContent = sellingPlan.name;
                    if (index === 0) option.selected = true;
                    select.appendChild(option);
                }
            });

            // Trigger change event manually to notify pmd-subscription-group-discount
            select.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    class PMDSubscriptionGroupDiscount extends HTMLElement {
        static get observedAttributes() {
            return ['type']; // 'percent' (default) or 'fixed'
        }

        constructor() {
            super();
            this._currentVariant = null;
            this._selectedSellingPlanId = null;
        }

        connectedCallback() {
            const parentProduct = this.closest('pmd-product');
            if (parentProduct) {
                parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            }

            const subscriptionBox = this.closest('pmd-subscription-box');
            if (subscriptionBox) {
                subscriptionBox.addEventListener('pmd:subscription:variant-change', this._onVariantChange.bind(this));
            }

            const subscriptionGroup = this.closest('pmd-subscription-group');
            if (subscriptionGroup) {
                subscriptionGroup.addEventListener('change', this._onSellingPlanChange.bind(this));
            }

            requestAnimationFrame(() => {
                if (parentProduct && parentProduct.selectedVariant) {
                    this._currentVariant = parentProduct.selectedVariant;
                }

                if (subscriptionGroup) {
                    const select = subscriptionGroup.querySelector('select');
                    if (select && select.value) {
                        this._selectedSellingPlanId = parseInt(select.value, 10);
                    }
                }
                
                this._updateDiscount();
            });
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (oldValue !== newValue && name === 'type') {
                this._updateDiscount();
            }
        }

        _onVariantChange(e) {
            this._currentVariant = e.detail.variant;
            // Reset selected selling plan when variant changes as allocations might be different
            const subscriptionGroup = this.closest('pmd-subscription-group');
            if (subscriptionGroup) {
                const select = subscriptionGroup.querySelector('select');
                if (select) {
                    // If there's a selected option, use it, otherwise null
                    this._selectedSellingPlanId = select.value ? parseInt(select.value, 10) : null;
                }
            }
            this._updateDiscount();
        }

        _onSellingPlanChange(e) {
            if (e.target.tagName === 'SELECT') {
                this._selectedSellingPlanId = e.target.value ? parseInt(e.target.value, 10) : null;
                this._updateDiscount();
            }
        }

        _updateDiscount() {
            if (!this._currentVariant) return;

            const parentProduct = this.closest('pmd-product');
            if (!parentProduct || !parentProduct.productData) return;

            const productData = parentProduct.productData;
            
            // Ensure this._selectedSellingPlanId is in sync with the select if it exists
            const subscriptionGroup = this.closest('pmd-subscription-group');
            if (subscriptionGroup) {
                const select = subscriptionGroup.querySelector('select');
                if (select) {
                    this._selectedSellingPlanId = select.value ? parseInt(select.value, 10) : null;
                }
            }

            // Find maximum discount from all available allocations for the current variant
            let bestSellingPlan = null;
            let bestDiscountValue = -1;

            if (this._currentVariant.selling_plan_allocations) {
                this._currentVariant.selling_plan_allocations.forEach(allocation => {
                    let sellingPlan = null;
                    productData.selling_plan_groups.forEach(group => {
                        const found = group.selling_plans.find(p => p.id === allocation.selling_plan_id);
                        if (found) sellingPlan = found;
                    });

                    if (sellingPlan && sellingPlan.price_adjustments && sellingPlan.price_adjustments.length > 0) {
                        const adj = sellingPlan.price_adjustments[0];
                        let currentDiscountValue = 0;
                        if (adj.value_type === 'percentage') {
                            currentDiscountValue = adj.value;
                        } else if (adj.value_type === 'fixed_amount') {
                            currentDiscountValue = (adj.value / this._currentVariant.price) * 100;
                        } else if (adj.value_type === 'price') {
                            currentDiscountValue = (Math.max(0, this._currentVariant.price - adj.value) / this._currentVariant.price) * 100;
                        }

                        if (currentDiscountValue > bestDiscountValue) {
                            bestDiscountValue = currentDiscountValue;
                            bestSellingPlan = sellingPlan;
                        }
                    }
                });
            }

            const sellingPlanId = this._selectedSellingPlanId;
            let sellingPlan = null;

            if (sellingPlanId) {
                // If specific plan selected, use it
                if (productData.selling_plan_groups) {
                    productData.selling_plan_groups.forEach(group => {
                        const found = group.selling_plans.find(p => p.id === sellingPlanId);
                        if (found) sellingPlan = found;
                    });
                }
            } 
            
            // Fallback to best selling plan if none selected or selection didn't yield a plan
            if (!sellingPlan) {
                sellingPlan = bestSellingPlan;
            }

            if (!sellingPlan || !sellingPlan.price_adjustments || sellingPlan.price_adjustments.length === 0) {
                this.textContent = '';
                this.setAttribute('hidden', '');
                return;
            }

            const adjustment = sellingPlan.price_adjustments[0];

            // A plan can carry a zero (or negative) adjustment - this store has
            // fixed_amount plans with value 0. That is not a discount, and rendering
            // "0%" / "$0.00" is worse than rendering nothing, so hide instead.
            const savingInCents =
                adjustment.value_type === 'price'
                    ? this._currentVariant.price - adjustment.value
                    : adjustment.value;
            if (!(savingInCents > 0)) {
                this.textContent = '';
                this.setAttribute('hidden', '');
                return;
            }
            // `auto` (the default) renders the discount in the selling plan's OWN
            // value_type, so a fixed_amount plan shows a currency amount instead of
            // being silently converted to a percentage. `percent` and `fixed` remain
            // available as explicit overrides.
            //
            // `price` sets an absolute price rather than a discount, so there is no
            // native unit to echo; it is reported as a percentage saved.
            let type = this.getAttribute('type') || 'auto';
            if (type === 'auto') {
                type = adjustment.value_type === 'fixed_amount' ? 'fixed' : 'percent';
            }
            let discountText = '';
            const priceFormat = getPriceFormat(this);

            if (adjustment.value_type === 'percentage') {
                if (type === 'percent') {
                    discountText = `${adjustment.value}%`;
                } else if (type === 'fixed') {
                    // Calculate fixed discount: (variant price * percentage) / 100
                    const fixedValue = (this._currentVariant.price * adjustment.value) / 100;
                    discountText = formatPrice(fixedValue, priceFormat);
                }
            } else if (adjustment.value_type === 'fixed_amount') {
                if (type === 'fixed') {
                    discountText = formatPrice(adjustment.value, priceFormat);
                } else if (type === 'percent') {
                    // Calculate percentage discount: (fixed value / variant price) * 100
                    const percentValue = Math.round((adjustment.value / this._currentVariant.price) * 100);
                    discountText = `${percentValue}%`;
                }
            } else if (adjustment.value_type === 'price') {
                 if (type === 'fixed') {
                    const fixedValue = Math.max(0, this._currentVariant.price - adjustment.value);
                    discountText = formatPrice(fixedValue, priceFormat);
                } else if (type === 'percent') {
                    const percentValue = Math.round((Math.max(0, this._currentVariant.price - adjustment.value) / this._currentVariant.price) * 100);
                    discountText = `${percentValue}%`;
                }
            }

            if (discountText) {
                this.textContent = discountText;
                this.removeAttribute('hidden');
            } else {
                this.textContent = '';
                this.setAttribute('hidden', '');
            }
        }
    }

    class PMDSubscriptionGroupPrice extends HTMLElement {
        constructor() {
            super();
            this._currentVariant = null;
            this._selectedSellingPlanId = null;
        }

        connectedCallback() {
            const parentProduct = this.closest('pmd-product');
            const subscriptionGroup = this.closest('pmd-subscription-group');

            if (parentProduct) {
                parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            }

            if (subscriptionGroup) {
                subscriptionGroup.addEventListener('change', this._onSellingPlanChange.bind(this));
            }

            requestAnimationFrame(() => {
                if (parentProduct && parentProduct.selectedVariant) {
                    this._currentVariant = parentProduct.selectedVariant;
                }
                if (subscriptionGroup) {
                    const select = subscriptionGroup.querySelector('select');
                    if (select && select.value) {
                        this._selectedSellingPlanId = parseInt(select.value, 10);
                    }
                }
                this._updatePrice();
            });
        }

        _onVariantChange(e) {
            this._currentVariant = e.detail.variant;
            this._updatePrice();
        }

        _onSellingPlanChange(e) {
            if (e.target.tagName !== 'SELECT') return;

            this._selectedSellingPlanId = e.target.value ? parseInt(e.target.value, 10) : null;

            // Pull fresh variant since select rebuilds happen on variant change
            const parentProduct = this.closest('pmd-product');
            if (parentProduct && parentProduct.selectedVariant) {
                this._currentVariant = parentProduct.selectedVariant;
            }

            this._updatePrice();
        }

        _updatePrice() {
            if (!this._currentVariant) return;

            const allocations = this._currentVariant.selling_plan_allocations;
            if (!allocations || allocations.length === 0) {
                this.textContent = '';
                this.setAttribute('hidden', '');
                return;
            }

            let allocation = this._selectedSellingPlanId
                ? allocations.find(a => a.selling_plan_id === this._selectedSellingPlanId)
                : null;

            if (!allocation) {
                allocation = allocations[0];
            }

            if (!allocation || allocation.price == null) {
                this.textContent = '';
                this.setAttribute('hidden', '');
                return;
            }

            const priceFormat = getPriceFormat(this);
            this.textContent = formatPrice(allocation.price, priceFormat);
            this.removeAttribute('hidden');
        }
    }

    class PMDFinalPrice extends HTMLElement {
        constructor() {
            super();
            this._currentVariant = null;
            this._selectedSellingPlanId = null;
            this._purchaseType = 'one-time';
            this._initialized = false;
        }

        connectedCallback() {
            const parentProduct = resolveProductHost(this);
            if (!parentProduct) return;

            parentProduct.addEventListener('pmd:product:variant-change', this._onVariantChange.bind(this));
            parentProduct.addEventListener('pmd:subscription:type-change', this._onTypeChange.bind(this));
            parentProduct.addEventListener('change', this._onSellingPlanChange.bind(this));

            requestAnimationFrame(() => {
                if (parentProduct.selectedVariant) {
                    this._currentVariant = parentProduct.selectedVariant;
                }

                const subscriptionBox = parentProduct.querySelector('pmd-subscription-box');
                if (subscriptionBox) {
                    const checkedRadio = subscriptionBox.querySelector('input[type="radio"]:checked');
                    this._purchaseType = checkedRadio ? checkedRadio.value : 'one-time';
                }

                const subscriptionGroup = parentProduct.querySelector('pmd-subscription-group');
                if (subscriptionGroup) {
                    const select = subscriptionGroup.querySelector('select');
                    if (select && select.value) {
                        this._selectedSellingPlanId = parseInt(select.value, 10);
                    }
                }

                this._initialized = true;
                this._updatePrice();
            });
        }

        _onVariantChange(e) {
            this._currentVariant = e.detail.variant;
            if (this._initialized) this._updatePrice();
        }

        _onTypeChange(e) {
            this._purchaseType = e.detail.type;
            this._updatePrice();
        }

        _onSellingPlanChange(e) {
            if (e.target.tagName !== 'SELECT' || !e.target.closest('pmd-subscription-group')) return;
            this._selectedSellingPlanId = e.target.value ? parseInt(e.target.value, 10) : null;
            const parentProduct = resolveProductHost(this);
            if (parentProduct && parentProduct.selectedVariant) {
                this._currentVariant = parentProduct.selectedVariant;
            }
            this._updatePrice();
        }

        _updatePrice() {
            if (!this._currentVariant) return;

            let price;

            if (this._purchaseType === 'subscription') {
                const allocations = this._currentVariant.selling_plan_allocations;
                if (allocations && allocations.length > 0) {
                    let allocation = this._selectedSellingPlanId
                        ? allocations.find(a => a.selling_plan_id === this._selectedSellingPlanId)
                        : null;
                    if (!allocation) allocation = allocations[0];
                    price = allocation.price;
                } else {
                    price = this._currentVariant.price;
                }
            } else {
                price = this._currentVariant.price;
            }

            const target = this.querySelector('[data-price-target]') || this;
            target.textContent = formatPrice(price, getPriceFormat(this));
        }
    }

    /**
     * Connects a pmd-product to a pmd-slider gallery so the gallery reacts to
     * variant selection.
     *
     * Modes:
     * - "scroll" (default): scrolls the gallery to the slide matching the selected
     *   variant's featured media (slide `media-id` attribute vs `variant.featured_media.id`,
     *   with a fallback to `variant.featured_image.position`).
     * - "filter": shows only slides whose `data-media-group` matches the selected
     *   value of the option named by `group-option` (e.g. "Color"). Slides without
     *   a `data-media-group` are always shown. The same filter is applied to the
     *   linked pmd-slider-thumbs.
     */
    class PMDVariantMedia extends HTMLElement {
        connectedCallback() {
            this._initialized = false;
            this._onVariantChange = (e) => {
                if (!this._matchesProduct(e)) return;
                this._apply(e.detail.variant, e.detail.options);
            };
            window.addEventListener('pmd:product:variant-change', this._onVariantChange);

            // Late join: the product may have initialized before this element connected
            requestAnimationFrame(() => {
                if (this._initialized) return;
                const product = this._getProduct();
                if (product && product.selectedVariant) {
                    this._apply(product.selectedVariant, product.currentOptions);
                }
            });
        }

        disconnectedCallback() {
            window.removeEventListener('pmd:product:variant-change', this._onVariantChange);
        }

        _getProduct() {
            const productId = this.getAttribute('data-product');
            if (productId) return document.getElementById(productId);
            return this.closest('pmd-product') || document.querySelector('pmd-product');
        }

        _matchesProduct(e) {
            const productId = this.getAttribute('data-product');
            if (productId) {
                return e.detail.productId === productId || (e.target && e.target.id === productId);
            }
            const closest = this.closest('pmd-product');
            if (closest) return e.target === closest;
            return true; // single-product pages need no explicit link
        }

        _getSlider() {
            const sliderId = this.getAttribute('data-slider');
            return sliderId ? document.getElementById(sliderId) : null;
        }

        _getThumbs() {
            const thumbsId = this.getAttribute('data-thumbs');
            if (thumbsId) return document.getElementById(thumbsId);
            const sliderId = this.getAttribute('data-slider');
            if (!sliderId) return null;
            const escapedId = window.CSS && CSS.escape ? CSS.escape(sliderId) : sliderId;
            return document.querySelector(`pmd-slider-thumbs[data-slider="${escapedId}"]`);
        }

        _getOriginalSlides(container) {
            return Array.from(container.querySelectorAll('pmd-slide:not([cloned])'));
        }

        _getVisibleSlides(container) {
            return Array.from(container.querySelectorAll('pmd-slide:not([cloned]):not([hidden])'));
        }

        _apply(variant, options) {
            const slider = this._getSlider();
            if (!slider || !variant) return;

            // Jump instantly on the initial load; animate on user-driven changes
            const behavior = this._initialized
                ? (this.getAttribute('scroll-behavior') || 'smooth')
                : 'instant';
            this._initialized = true;

            if (this.getAttribute('mode') === 'filter') {
                this._applyFilter(slider, variant, options || {});
            } else {
                this._applyScroll(slider, variant, behavior);
            }
        }

        /**
         * Index of the variant's featured media within `slides`.
         * `allSlides` is the unfiltered list used to resolve the position fallback.
         */
        _findMediaIndex(slides, variant, allSlides) {
            const mediaId = variant.featured_media && variant.featured_media.id;
            if (mediaId !== undefined && mediaId !== null) {
                const index = slides.findIndex(slide => slide.getAttribute('media-id') === String(mediaId));
                if (index !== -1) return index;
            }

            // Fallback: image position (aligns when the gallery renders product
            // media in order and all media are images)
            const image = variant.featured_image;
            if (image && image.position >= 1) {
                const source = allSlides || slides;
                const targetSlide = source[image.position - 1];
                const index = targetSlide ? slides.indexOf(targetSlide) : -1;
                if (index !== -1) return index;
            }

            return -1;
        }

        _applyScroll(slider, variant, behavior) {
            const visibleSlides = this._getVisibleSlides(slider);
            const index = this._findMediaIndex(visibleSlides, variant, this._getOriginalSlides(slider));
            if (index === -1) return; // variant has no gallery media: stay put

            if (slider.state && slider.state.currentIndex === index) return;
            if (typeof slider.scrollToIndex === 'function') {
                slider.scrollToIndex(index, behavior);
            }
        }

        _applyFilter(slider, variant, options) {
            const groupOption = this.getAttribute('group-option');
            const normalize = (value) => String(value == null ? '' : value).trim().toLowerCase();
            const selected = normalize(groupOption ? options[groupOption] : '');

            const filterContainer = (container) => {
                const slides = this._getOriginalSlides(container);
                const visibility = slides.map(slide => {
                    const group = slide.getAttribute('data-media-group');
                    if (!group || !selected) return true; // ungrouped media always shows
                    return normalize(group) === selected;
                });
                // Never filter down to an empty gallery
                if (!visibility.some(Boolean)) visibility.fill(true);
                slides.forEach((slide, i) => slide.toggleAttribute('hidden', !visibility[i]));
            };

            filterContainer(slider);

            // Land on the variant's featured media if it survived the filter, else the first slide
            const visibleSlides = this._getVisibleSlides(slider);
            const mediaIndex = this._findMediaIndex(visibleSlides, variant, this._getOriginalSlides(slider));
            const targetIndex = mediaIndex === -1 ? 0 : mediaIndex;

            if (typeof slider.refresh === 'function') {
                slider.refresh(targetIndex);
            }

            const thumbs = this._getThumbs();
            if (thumbs) {
                filterContainer(thumbs);
                if (typeof thumbs.scrollToIndex === 'function') {
                    requestAnimationFrame(() => thumbs.scrollToIndex(targetIndex));
                }
            }
        }
    }

    customElements.define('pmd-product', PMDProduct);
    customElements.define('pmd-product-option', PMDProductOption);
    customElements.define('pmd-option-value', PMDOptionValue);
    customElements.define('pmd-variant-title', PMDVariantTitle);
    customElements.define('pmd-price-regular', PMDPriceRegular);
    customElements.define('pmd-price-compare-at', PMDPriceCompareAt);
    customElements.define('pmd-atc-button', PMDATCButton);
    customElements.define('pmd-subscription-box', PMDSubscriptionBox);
    customElements.define('pmd-one-time-purchase', PMDOneTimePurchase);
    customElements.define('pmd-subscription-group', PMDSubscriptionGroup);
    customElements.define('pmd-subscription-group-discount', PMDSubscriptionGroupDiscount);
    customElements.define('pmd-subscription-group-price', PMDSubscriptionGroupPrice);
    customElements.define('pmd-final-price', PMDFinalPrice);
    customElements.define('pmd-variant-media', PMDVariantMedia);

    class PMDSelectIcon extends HTMLElement {
        connectedCallback() {
            requestAnimationFrame(() => {
                this.icon = this.querySelector('img');
            });
            this.closest('pmd-select-container')?.addEventListener('pmd:select-container:change', this.onSelectChange.bind(this));
        }

        onSelectChange(e) {
            if (this.icon && e.detail.icon)
                this.icon.src = e.detail.icon;
        }
    }

    class PMDSelectContainer extends HTMLElement {
        connectedCallback() {
            requestAnimationFrame(() => {
                this.select = this.querySelector('select');
                this.select.addEventListener('change', this.onSelectChange.bind(this));
                this.onSelectChange();
            });
        }

        onSelectChange() {
            if(this.select)
                this.dispatchEvent(new CustomEvent('pmd:select-container:change', {
                    detail: this.select.querySelector('option:checked').dataset
                }));
        }
    }

    customElements.define('pmd-select-icon', PMDSelectIcon);
    customElements.define('pmd-select-container', PMDSelectContainer);

    function styleInject(css, ref) {
      if ( ref === void 0 ) ref = {};
      var insertAt = ref.insertAt;

      if (typeof document === 'undefined') { return; }

      var head = document.head || document.getElementsByTagName('head')[0];
      var style = document.createElement('style');
      style.type = 'text/css';

      if (insertAt === 'top') {
        if (head.firstChild) {
          head.insertBefore(style, head.firstChild);
        } else {
          head.appendChild(style);
        }
      } else {
        head.appendChild(style);
      }

      if (style.styleSheet) {
        style.styleSheet.cssText = css;
      } else {
        style.appendChild(document.createTextNode(css));
      }
    }

    var css_248z = "pmd-slider{--slider-slides-per-view:1;--slider-slider-gap:0px;--slider-slide-aspect-ratio:1;--slider-draggable:false;--slider-infinite:false;--slider-autoplay-enabled:false;--slider-autoplay-delay:3000;--slider-autoplay-direction:default;cursor:default;display:flex;flex-direction:row;overflow:auto;position:relative;scroll-behavior:smooth;scroll-snap-type:x mandatory;scrollbar-width:none;user-select:none;width:100%}pmd-slider[draggable-enabled=true]{cursor:grab}pmd-slider.is-dragging{cursor:grabbing;scroll-behavior:auto!important;scroll-snap-type:none}pmd-slider::-webkit-scrollbar{display:none}pmd-slider[style*=\"--slider-slide-aspect-ratio: auto\"],pmd-slider[style*=\"--slider-slide-aspect-ratio:auto\"]{align-items:stretch}pmd-slider[style*=\"--slider-slide-aspect-ratio: auto\"] pmd-slide,pmd-slider[style*=\"--slider-slide-aspect-ratio:auto\"] pmd-slide{display:flex;padding-bottom:0}pmd-slider[style*=\"--slider-slide-aspect-ratio: auto\"] pmd-slide pmd-slide-content,pmd-slider[style*=\"--slider-slide-aspect-ratio:auto\"] pmd-slide pmd-slide-content{position:relative}pmd-slide[hidden]{display:none!important}pmd-slide{--slide-width:calc((100% - var(--slider-slider-gap)*(var(--slider-slides-per-view) - 1))/var(--slider-slides-per-view));box-sizing:border-box;display:block;flex-shrink:0;height:0;margin-right:var(--slider-slider-gap);padding-bottom:calc(1/(var(--slider-slide-aspect-ratio))*var(--slide-width));position:relative;scroll-snap-align:start;width:var(--slide-width)}pmd-slider pmd-slide:last-child{margin-right:0}pmd-slide pmd-slide-content{height:100%;left:0;position:absolute;top:0;width:100%}pmd-slider-nav{cursor:pointer}pmd-slider-nav:has(button:disabled),pmd-slider-nav[disabled]{cursor:not-allowed}pmd-slider-nav button{pointer-events:none}pmd-slider-nav button:disabled{cursor:not-allowed;opacity:.5}pmd-slider-thumbs{--slider-slides-per-view:4;--slider-slider-gap:10px;--slider-slide-aspect-ratio:1;display:flex;overflow:auto;position:relative;scroll-behavior:smooth;scrollbar-width:none;user-select:none;width:100%}pmd-slider-thumbs[draggable-enabled=true],pmd-slider-thumbs[draggable-enabled=true] pmd-slide{cursor:grab}pmd-slider-thumbs.is-dragging,pmd-slider-thumbs.is-dragging pmd-slide{cursor:grabbing;scroll-behavior:auto!important}pmd-slider-thumbs::-webkit-scrollbar{display:none}pmd-slider-thumbs pmd-slide{--slide-width:calc((100% - var(--slider-slider-gap)*(var(--slider-slides-per-view) - 1))/var(--slider-slides-per-view));cursor:pointer;flex-shrink:0;height:0;margin-right:var(--slider-slider-gap);padding-bottom:calc(1/(var(--slider-slide-aspect-ratio))*var(--slide-width));width:var(--slide-width)}pmd-slider-thumbs pmd-slide:last-child{margin-right:0}pmd-slider-thumbs[draggable-enabled=true] pmd-slide:active{cursor:grabbing}pmd-slider-pagination{display:flex;gap:8px;justify-content:center}.pmd-pagination-dot{background-color:transparent;border:1px solid #ccc;border-radius:50%;cursor:pointer;height:10px;padding:0;transition:all .3s ease;width:10px}.pmd-pagination-dot.is-active{background-color:#333;border-color:#333;transform:scale(1.2)}pmd-slider-progress-bar{background-color:var(--pmd-slider-progress-bar-bg,#eee);display:block;height:var(--pmd-slider-progress-bar-height,4px);overflow:hidden;position:relative;width:100%}.pmd-slider-progress-bar-inner{background-color:var(--pmd-slider-progress-bar-progress-bg,#333);height:100%;transition:width .1s ease-out;width:0}pmd-slider-thumbs[direction=vertical],pmd-slider[direction=vertical]{flex-direction:column;overflow-x:hidden;overflow-y:auto;scroll-snap-type:y mandatory}pmd-slider-thumbs[direction=vertical].is-dragging,pmd-slider[direction=vertical].is-dragging{scroll-snap-type:none}pmd-slider-thumbs[direction=vertical]>pmd-slide,pmd-slider[direction=vertical]>pmd-slide{--slide-height:calc((100% - var(--slider-slider-gap)*(var(--slider-slides-per-view) - 1))/var(--slider-slides-per-view));height:var(--slide-height);margin-bottom:var(--slider-slider-gap);margin-right:0;padding-bottom:0;scroll-snap-align:start;width:100%}pmd-slider-thumbs[direction=vertical]>pmd-slide:last-child,pmd-slider[direction=vertical]>pmd-slide:last-child{margin-bottom:0}pmd-slider-thumbs[direction=vertical]>pmd-slide>pmd-slide-content,pmd-slider[direction=vertical]>pmd-slide>pmd-slide-content{height:100%;inset:0;position:absolute;width:100%}pmd-slider-progress-bar[direction=vertical]{height:100%;width:var(--pmd-slider-progress-bar-height,4px)}pmd-slider-progress-bar[direction=vertical] .pmd-slider-progress-bar-inner{height:0;transition:height .1s ease-out;width:100%}pmd-slider-thumbs[draggable-enabled=true] a,pmd-slider-thumbs[draggable-enabled=true] img,pmd-slider-thumbs[draggable-enabled=true] picture,pmd-slider-thumbs[draggable-enabled=true] svg,pmd-slider[draggable-enabled=true] a,pmd-slider[draggable-enabled=true] img,pmd-slider[draggable-enabled=true] picture,pmd-slider[draggable-enabled=true] svg{-webkit-user-drag:none;user-drag:none}";
    styleInject(css_248z);

    class PmdSlide extends HTMLElement {
      connectedCallback() {
        // A classic (non-deferred) script upgrades this element mid-parse, before
        // its children exist; wrapping then would create an empty duplicate
        // pmd-slide-content. Defer until the DOM is complete in that case.
        if (!this.firstChild && document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this._wrapContent(), { once: true });
          return;
        }
        this._wrapContent();
      }

      _wrapContent() {
        if (this.querySelector('pmd-slide-content')) return;

        const content = document.createElement('pmd-slide-content');
        while (this.firstChild) {
          content.appendChild(this.firstChild);
        }
        this.appendChild(content);
      }
    }

    class PmdSlideContent extends HTMLElement {
      connectedCallback() {
      }
    }

    class PmdSlider extends HTMLElement {
      constructor() {
        super();
        this.state = {
          currentIndex: 0
        };
      }

      // Slides participating in layout/index math. [hidden] slides are filtered
      // out (e.g. by pmd-variant-media) and must not affect any index calculation.
      _getSlides() {
        return Array.from(this.querySelectorAll('pmd-slide:not([hidden])'));
      }

      _getOriginalSlides() {
        return Array.from(this.querySelectorAll('pmd-slide:not([cloned]):not([hidden])'));
      }

      // --- Axis abstraction -------------------------------------------------
      // Everything below reads and writes the scroll axis through these helpers,
      // so one code path drives both horizontal and vertical sliders. Direction
      // comes from --slider-direction and is mirrored onto a [direction]
      // attribute because CSS cannot select on a custom property value.
      get _isVertical() {
        return this.getAttribute('direction') === 'vertical';
      }

      get _scrollPos() {
        return this._isVertical ? this.scrollTop : this.scrollLeft;
      }

      set _scrollPos(value) {
        if (this._isVertical) this.scrollTop = value;
        else this.scrollLeft = value;
      }

      get _viewportSize() {
        return this._isVertical ? this.offsetHeight : this.offsetWidth;
      }

      get _maxScroll() {
        return this._isVertical
          ? this.scrollHeight - this.clientHeight
          : this.scrollWidth - this.clientWidth;
      }

      _slideStart(slide) {
        return this._isVertical ? slide.offsetTop : slide.offsetLeft;
      }

      _slideSize(slide) {
        return this._isVertical ? slide.offsetHeight : slide.offsetWidth;
      }

      _pointerPos(e) {
        return this._isVertical ? e.pageY : e.pageX;
      }

      _checkDirection() {
        const isVertical = getComputedStyle(this)
          .getPropertyValue('--slider-direction').trim() === 'vertical';
        this.setAttribute('direction', isVertical ? 'vertical' : 'horizontal');
      }

      connectedCallback() {
        this._checkDirection();
        this._checkInfinite();
        this._checkDraggable();
        this._checkAutoplay();
        this._checkAspectRatio();

        this._mutationObserver = new MutationObserver((mutations) => {
          let shouldUpdateHeight = false;
          mutations.forEach(mutation => {
            if (mutation.type === 'childList') shouldUpdateHeight = true;
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
               // Basic check if style attribute change might affect aspect ratio
               shouldUpdateHeight = true;
            }
          });
          if (shouldUpdateHeight) {
            this._checkInfinite();
            this._checkAspectRatio();
          }
        });
        this._mutationObserver.observe(this, { childList: true, attributes: true, attributeFilter: ['style'] });

        // Maintain current slide position on resize
        this._onResize = () => {
          this._checkDirection();
          this._checkInfinite();
          this._checkDraggable();
          this._checkAutoplay();
          this._checkAspectRatio();
          this.scrollToIndex();
        };
        window.addEventListener('resize', this._onResize);

        // Track scroll to update currentIndex and handle snapping
        this._onScroll = () => {
          this.updateCurrentIndex();
          this._handleInfiniteScroll();

          if (!this._isProgrammaticScroll && !this._isDragging) {
            this._resetAutoplay(2);
          }

          // Emulate scroll snap only if CSS snap is NOT active (i.e. when dragging is enabled)
          if (this.getAttribute('draggable-enabled') !== 'true') return;
        };
        this.addEventListener('scroll', this._onScroll);
        this.addEventListener('scroll', () => {
          this.dispatchEvent(new CustomEvent('pmd:slider:scroll', {
            bubbles: true,
            composed: true,
            detail: {
              sliderId: this.id,
              currentIndex: this.state.currentIndex
            }
          }));
        });


        this._onScrollEnd = () => {
            // On mobile, we avoid auto-snapping during/immediately after touch interaction
            // to let the browser's native momentum and our handleEnd logic finish.
            if (!this._isDragging && !this._isProgrammaticScroll && !this._isTouchInteraction) {
                this.snapToClosest();
            }
            this._isProgrammaticScroll = false;

            if (this._isDragging) return;

            const currentIdx = this._getClosestSlideIndex(this._scrollPos);
            const slides = this._getSlides();
            const originalCount = slides.length - (this._numClones * 2);

            if (currentIdx < this._numClones) {
                this._jumpToSlide(currentIdx + originalCount);
            } else if (currentIdx >= this._numClones + originalCount) {
                this._jumpToSlide(currentIdx - originalCount);
            }

            this.dispatchEvent(new CustomEvent('pmd:slider:scroll', {
                bubbles: true,
                composed: true,
                detail: {
                    sliderId: this.id,
                    currentIndex: this.state.currentIndex
                }
            }));
        };
        // Use a debounced scroll listener to detect scroll end for programmatic scrolls
        this._onScrollEndFallback = () => {
            if (!this._isProgrammaticScroll) return;

            clearTimeout(this._scrollFallbackTimeout);
            this._scrollFallbackTimeout = setTimeout(() => {
                this._onScrollEnd();
            }, 150);
        };

        if ('onscrollend' in this) {
            this.addEventListener('scrollend', this._onScrollEnd);
        } else {
            this.addEventListener('scroll', this._onScrollEndFallback);
        }

        // Touch events - We still listen for touch to handle next/prev flick if needed, 
        // but CSS snap handles the actual alignment.
        this._onTouchStart = (e) => {
          this._isTouchInteraction = true;
          clearTimeout(this._touchInteractionTimeout);
          // For touch, we only need handleStart to clear timers and set start variables
          // but we don't want to prevent default or set scroll-behavior to auto
          this.handleStart(this._pointerPos(e.touches[0]));
        };
        this._onTouchMove = (e) => {
            // We let the browser handle the scroll
        };
        this._onTouchEnd = (e) => {
          // Keep _isTouchInteraction true for a bit to let momentum/scroll events finish
          clearTimeout(this._touchInteractionTimeout);
          this._touchInteractionTimeout = setTimeout(() => {
            this._isTouchInteraction = false;
          }, 500);
          this.handleEnd();
        };

        this.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.addEventListener('touchmove', this._onTouchMove, { passive: true });
        this.addEventListener('touchend', this._onTouchEnd);

        // Mouse events
        this._onMouseDown = (e) => {
          if (this.getAttribute('draggable-enabled') !== 'true') return;
          this._isTouchInteraction = false; // Mouse interaction
          clearTimeout(this._touchInteractionTimeout);
          this.handleStart(this._pointerPos(e));
          this.classList.add('is-dragging');
        };
        this._onMouseMove = (e) => {
          if (!this._isDragging) return;
          e.preventDefault();
          this.handleMove(this._pointerPos(e));
        };
        this._onMouseUp = () => {
          if (!this._isDragging) return;
          // `is-dragging` is dropped by handleEnd() once the slider has settled, not
          // here: releasing snap this early lets the browser snap to whatever slide
          // is nearest, and the flick logic then advances again from there.
          this.handleEnd();
        };

        this.addEventListener('mousedown', this._onMouseDown);
        // Native HTML5 drag on images/links hijacks the pointer before our drag
        // logic sees it. Firefox ignores -webkit-user-drag, so cancel it here too.
        this._onDragStart = (e) => {
          if (this.getAttribute('draggable-enabled') !== 'true') return;
          e.preventDefault();
        };
        this.addEventListener('dragstart', this._onDragStart);
        window.addEventListener('mousemove', this._onMouseMove);
        window.addEventListener('mouseup', this._onMouseUp);

        this._resizeObserver = new ResizeObserver(() => this._onResize());
        this._resizeObserver.observe(this);
      }

      _checkDraggable() {
        // Check if the primary pointer is coarse (like a finger)
        const isTouchPrimary = window.matchMedia("(pointer: coarse)").matches;
        const isDraggableValue = getComputedStyle(this).getPropertyValue('--slider-draggable').trim() === 'true';
        
        // If device primary input is touch, we disable JS dragging and use CSS snap
        const isDraggable = !isTouchPrimary && isDraggableValue;
        this.setAttribute('draggable-enabled', isDraggable);
      }

      _checkInfinite() {
        const isInfinite = getComputedStyle(this).getPropertyValue('--slider-infinite').trim() === 'true';
        if (isInfinite && !this._isInfinite) {
          // A classic (non-deferred) script upgrades this element mid-parse,
          // before its slides exist. Wait for the full DOM before cloning.
          if (document.readyState === 'loading') {
            if (!this._infiniteRetryScheduled) {
              this._infiniteRetryScheduled = true;
              document.addEventListener('DOMContentLoaded', () => this._checkInfinite(), { once: true });
            }
            return;
          }
          this._isInfinite = true;
          this._setupInfinite();
        } else if (!isInfinite && this._isInfinite) {
          this._isInfinite = false;
          this._cleanupInfinite();
        }
      }

      _checkAspectRatio() {
        const style = getComputedStyle(this);
        const aspectRatio = style.getPropertyValue('--slider-slide-aspect-ratio').trim();
        
        if (aspectRatio === 'auto' && !this._isVertical) {
          this._updateAutoHeight();
        } else {
          // Vertically the slider's own height IS the axis that
          // --slider-slides-per-view divides up, and the consumer sets it. Stripping
          // it here would collapse every slide to zero.
          if (!this._isVertical) {
            this.style.removeProperty('height');
          }
          const slides = this.querySelectorAll('pmd-slide');
          slides.forEach(slide => slide.style.removeProperty('height'));
        }
      }

      _updateAutoHeight() {
        const slides = this._getOriginalSlides();
        if (slides.length === 0) return;

        // Reset heights to get natural height
        const allSlides = this.querySelectorAll('pmd-slide');
        allSlides.forEach(slide => slide.style.height = 'auto');
        
        // Use requestAnimationFrame twice to ensure layout is settled in all browsers
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            let maxHeight = 0;
            slides.forEach(slide => {
              const height = slide.getBoundingClientRect().height;
              if (height > maxHeight) maxHeight = height;
            });

            if (maxHeight > 0) {
              // Apply max height to all slides (including clones)
              allSlides.forEach(slide => {
                slide.style.height = `${maxHeight}px`;
              });
            }
          });
        });
      }

      _setupInfinite() {
        const originalSlides = this._getOriginalSlides();
        if (originalSlides.length === 0) {
          // No slides yet (e.g. they are injected later): reset the flag so the
          // MutationObserver's next _checkInfinite() can retry instead of sticking.
          this._isInfinite = false;
          return;
        }

        // A clone is a decorative duplicate of a slide. Anything that has to be
        // unique in the document must not survive the copy: ids become duplicates
        // (a WCAG 4.1.1 failure, and it breaks every label/aria reference pointing
        // at them), and Shopify's theme editor block attributes make the editor
        // bind to a node the section did not render.
        const stripIdentity = (clone) => {
          const nodes = [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')];
          nodes.forEach(node => {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
          });
        };

        // Clone slides
        const slidesPerView = Math.ceil(parseFloat(getComputedStyle(this).getPropertyValue('--slider-slides-per-view')) || 1);
        
        // We need enough clones to fill the view on both sides. 
        // Usually cloning all slides is safer if the number of slides is small.
        const numClones = Math.max(slidesPerView, originalSlides.length);

        // Clone at the end (to be placed at the beginning)
        for (let i = 0; i < numClones; i++) {
          const slideToClone = originalSlides[(originalSlides.length - numClones + i) % originalSlides.length];
          const clone = slideToClone.cloneNode(true);
          clone.setAttribute('cloned', 'true');
          stripIdentity(clone);
          clone.setAttribute('original-index', (originalSlides.length - numClones + i) % originalSlides.length);
          this.insertBefore(clone, originalSlides[0]);
        }

        // Clone at the beginning (to be placed at the end)
        for (let i = 0; i < numClones; i++) {
          const slideToClone = originalSlides[i % originalSlides.length];
          const clone = slideToClone.cloneNode(true);
          clone.setAttribute('cloned', 'true');
          stripIdentity(clone);
          clone.setAttribute('original-index', i % originalSlides.length);
          this.appendChild(clone);
        }

        this._numClones = numClones;
        
        // Initial scroll to the first real slide — must be an instant jump, so
        // temporarily disable smooth scroll-behavior like _jumpToSlide does
        requestAnimationFrame(() => {
          this._isProgrammaticScroll = true;
          const firstRealSlide = originalSlides[0];
          const originalScrollBehavior = this.style.scrollBehavior;
          this.style.scrollBehavior = 'auto';
          this._scrollPos = this._slideStart(firstRealSlide);
          this.style.scrollBehavior = originalScrollBehavior;
          setTimeout(() => {
            this._isProgrammaticScroll = false;
          }, 100);
        });
      }

      _cleanupInfinite() {
        const clonedSlides = this.querySelectorAll('pmd-slide[cloned]');
        clonedSlides.forEach(slide => slide.remove());
        this._numClones = 0;
      }

      _checkAutoplay() {
        const style = getComputedStyle(this);
        const isAutoplayEnabled = style.getPropertyValue('--slider-autoplay-enabled').trim() === 'true';
        const autoplayDelay = parseInt(style.getPropertyValue('--slider-autoplay-delay')) || 3000;
        const autoplayDirection = style.getPropertyValue('--slider-autoplay-direction').trim() || 'default';

        this._autoplayDelay = autoplayDelay;
        this._autoplayDirection = autoplayDirection;

        if (isAutoplayEnabled && !this._autoplayTimer) {
          this._startAutoplay();
        } else if (!isAutoplayEnabled && this._autoplayTimer) {
          this._stopAutoplay();
        }
      }

      _startAutoplay(delayMultiplier = 1) {
        this._stopAutoplay();
        if (getComputedStyle(this).getPropertyValue('--slider-autoplay-enabled').trim() !== 'true') return;

        this._autoplayTimer = setTimeout(() => {
          this._tickAutoplay();
        }, this._autoplayDelay * delayMultiplier);
      }

      _stopAutoplay() {
        if (this._autoplayTimer) {
          clearTimeout(this._autoplayTimer);
          this._autoplayTimer = null;
        }
      }

      _resetAutoplay(delayMultiplier = 1) {
        this._stopAutoplay();
        this._startAutoplay(delayMultiplier);
      }

      _tickAutoplay() {
        if (this._autoplayDirection === 'reverse') {
          const slides = this._getSlides();
          if (!this._isInfinite && this.state.currentIndex === 0) {
            // Optional: loop back to end if not infinite? 
            // User didn't specify, but usually reverse autoplay at start should go to end or stop.
            // Let's just use prev() which handles infinite logic.
            this.scrollToIndex(slides.length - 1);
          } else {
            this.prev();
          }
        } else {
          const slides = this._getSlides();
          const slidesPerView = parseFloat(getComputedStyle(this).getPropertyValue('--slider-slides-per-view')) || 1;
          const maxIndex = Math.max(0, slides.length - slidesPerView);
          
          if (!this._isInfinite && this.state.currentIndex >= Math.ceil(maxIndex)) {
            this.scrollToIndex(0);
          } else {
            this.next();
          }
        }
        this._startAutoplay();
      }

      _handleInfiniteScroll() {
        if (!this._isInfinite) return;

        const slides = this._getSlides();
        const numClones = this._numClones;
        const originalCount = slides.length - (numClones * 2);
        
        if (originalCount <= 0) return;

        const scrollPos = this._scrollPos;
        const firstRealSlide = slides[numClones];
        const lastRealSlide = slides[numClones + originalCount - 1];
        
        if (!firstRealSlide || !lastRealSlide) return;

        const viewportSize = this._viewportSize;
        const maxScrollPos = this._maxScroll;
        
        // Immediate swap if we hit physical boundaries
        if (scrollPos <= 0) {
            const currentIdx = this._getClosestSlideIndex(scrollPos);
            this._jumpToSlide(currentIdx + originalCount);
            return;
        }
        if (scrollPos >= maxScrollPos - 1) {
            const currentIdx = this._getClosestSlideIndex(scrollPos);
            this._jumpToSlide(currentIdx - originalCount);
            return;
        }

        // Swapping during programmatic scroll or dragging
        // We only swap programmatically if we've scrolled PAST the clones (into empty space or just far enough)
        // or if we are dragging.
        if (this._isDragging) {
            if (scrollPos < this._slideStart(firstRealSlide) - (viewportSize / 2)) {
                const currentIdx = this._getClosestSlideIndex(scrollPos);
                this._jumpToSlide(currentIdx + originalCount);
                return;
            } 
            else if (scrollPos > this._slideStart(lastRealSlide) + (this._slideSize(lastRealSlide) / 2)) {
                const currentIdx = this._getClosestSlideIndex(scrollPos);
                this._jumpToSlide(currentIdx - originalCount);
                return;
            }
        }
      }

      _jumpToSlide(index) {
        const slides = this._getSlides();
        const targetSlide = slides[index];
        if (!targetSlide) return;

        const oldScrollPos = this._scrollPos;

        // Calculate relative offset within the current slide to preserve it during jump
        const currentIdx = this._getClosestSlideIndex(oldScrollPos);
        const currentSlide = slides[currentIdx];
        let relativeOffset = 0;
        if (currentSlide) {
            relativeOffset = oldScrollPos - this._slideStart(currentSlide);
        }

        // Save current programmatic scroll state to restore it
        const wasProgrammatic = this._isProgrammaticScroll;
        this._isProgrammaticScroll = true;
        
        const isSnapEnabled = getComputedStyle(this).scrollSnapType !== 'none';
        if (isSnapEnabled) {
          this.style.scrollSnapType = 'none';
        }

        const originalScrollBehavior = this.style.scrollBehavior;
        this.style.scrollBehavior = 'auto';
        
        this._scrollPos = this._slideStart(targetSlide) + relativeOffset;
        const newScrollPos = this._scrollPos;

        // If we are dragging, we need to update _startScrollPos to account for the jump
        if (this._isDragging) {
            this._startScrollPos += (newScrollPos - oldScrollPos);
        }
        
        // Trigger update immediately so it's ready for any subsequent logic
        this.updateCurrentIndex();

        requestAnimationFrame(() => {
            if (isSnapEnabled) {
              this.style.scrollSnapType = '';
            }
            this.style.scrollBehavior = originalScrollBehavior;
            // Small delay to ensure browser processed the jump before allowing normal scroll events to take over
            setTimeout(() => {
                this._isProgrammaticScroll = wasProgrammatic;
            }, 50);
        });
      }

      _getClosestSlideIndex(scrollPos) {
        const slides = this._getSlides();
        if (slides.length === 0) return -1;

        const slidesPerView = parseFloat(getComputedStyle(this).getPropertyValue('--slider-slides-per-view')) || 1;
        const maxIndex = Math.max(0, slides.length - slidesPerView);
        const searchLimit = Math.ceil(maxIndex);

        let closestIndex = 0;
        let minDistance = Infinity;

        for (let i = 0; i < slides.length; i++) {
          if (i > searchLimit) break;
          
          const distance = Math.abs(this._slideStart(slides[i]) - scrollPos);
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = i;
          }
        }

        return closestIndex;
      }

      updateCurrentIndex() {
        const closestIndex = this._getClosestSlideIndex(this._scrollPos);
        if (closestIndex === -1) return;

        let displayIndex = closestIndex;
        if (this._isInfinite) {
          const slides = this._getSlides();
          const numClones = this._numClones;
          const originalCount = slides.length - (numClones * 2);
          
          if (closestIndex < numClones) {
            displayIndex = (closestIndex - numClones + originalCount) % originalCount;
          } else if (closestIndex >= numClones + originalCount) {
            displayIndex = (closestIndex - numClones) % originalCount;
          } else {
            displayIndex = closestIndex - numClones;
          }
        }

        if (this.state.currentIndex !== displayIndex) {
          this.state.currentIndex = displayIndex;
          this.dispatchEvent(new CustomEvent('pmd:slider:scroll', {
            bubbles: true,
            composed: true,
            detail: {
              sliderId: this.id,
              currentIndex: this.state.currentIndex
            }
          }));
        }
      }

      disconnectedCallback() {
        this._stopAutoplay();
        window.removeEventListener('resize', this._onResize);
        this.removeEventListener('scroll', this._onScroll);
        this.removeEventListener('scroll', this._onScrollEndFallback);
        this.removeEventListener('touchstart', this._onTouchStart);
        this.removeEventListener('touchmove', this._onTouchMove);
        this.removeEventListener('touchend', this._onTouchEnd);
        this.removeEventListener('mousedown', this._onMouseDown);
        this.removeEventListener('dragstart', this._onDragStart);
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mouseup', this._onMouseUp);
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
        }
        if (this._mutationObserver) {
          this._mutationObserver.disconnect();
        }
      }

      handleStart(pointerPos) {
        this._isDragging = true;
        this._startPointer = pointerPos;
        this._startScrollPos = this._scrollPos;
        this._startTime = Date.now();
        
        // Stop any ongoing smooth scroll or snapping
        if (!this._isTouchInteraction) {
            this.style.scrollBehavior = 'auto';
        }
        this._isProgrammaticScroll = false;
        clearTimeout(this._snapTimeout);
        clearTimeout(this._interactionEndTimeout);
        this._stopAutoplay();
      }

      handleMove(pointerPos) {
        if (!this._isDragging || this._isTouchInteraction) return;
        const walk = (pointerPos - this._startPointer);
        this._scrollPos = this._startScrollPos - walk;
      }

      handleEnd() {
        if (!this._isDragging) return;
        const isTouch = this._isTouchInteraction;
        this._isDragging = false;
        this.style.scrollBehavior = ''; // Restore original behavior

        const endTime = Date.now();
        const duration = endTime - this._startTime;
        const distance = this._scrollPos - this._startScrollPos;

        this._resetAutoplay(2);

        // Small delay to ensure any native momentum start doesn't immediately fight with our snapping
        clearTimeout(this._interactionEndTimeout);
        this._interactionEndTimeout = setTimeout(() => {
            const isDraggable = this.getAttribute('draggable-enabled') === 'true';
            
            // If it's a mouse interaction and draggable is enabled, we use our JS logic.
            // If it's a touch interaction, we let CSS snap handle it.
            
            if (isDraggable && !isTouch) {
                // Fast swipe (flick) detection for mouse
                if (duration < 300 && Math.abs(distance) > 50) {
                    if (distance > 0) {
                        this.next();
                    } else {
                        this.prev();
                    }
                } else {
                    // Slow drag or short movement for mouse
                    this.snapToClosest();
                }
            }
            // For touch, we do nothing and let CSS Scroll Snap take over.

            // Snapping comes back only now that the slider has been placed, so the
            // browser's snap target and ours agree. Touch never sets the class.
            this.classList.remove('is-dragging');
        }, 10);
      }

      snapToClosest() {
        const slides = this._getSlides();
        if (slides.length === 0) return;
        
        const slidesPerView = parseFloat(getComputedStyle(this).getPropertyValue('--slider-slides-per-view')) || 1;
        const maxIndex = Math.max(0, slides.length - slidesPerView);
        const maxScrollPos = this._maxScroll;

        let targetIndex;

        // If we are scrolled near the end, snap to the maxIndex
        if (this._scrollPos > maxScrollPos - 10 && !this._isInfinite) {
            targetIndex = Math.round(maxIndex);
        } else {
            targetIndex = this._getClosestSlideIndex(this._scrollPos);
            // Clamp to maxIndex for non-end snapping
            if (targetIndex > maxIndex && !this._isInfinite) {
                targetIndex = Math.floor(maxIndex);
            }
        }

        if (targetIndex !== -1) {
          this._scrollToIndexRaw(targetIndex);
        }
      }

      next() {
        this._resetAutoplay(2);
        const slides = this._getSlides();
        const slidesPerView = parseFloat(getComputedStyle(this).getPropertyValue('--slider-slides-per-view')) || 1;
        const maxIndex = Math.max(0, slides.length - slidesPerView);
        
        // Find next index based on current scroll position
        const currentScroll = this._scrollPos;
        let nextIndex = 0;
        
        // If we are at the very end of physical scroll and infinite is enabled, 
        // we should have already jumped, but if not, let's force a jump or handle it.
        if (this._isInfinite) {
            const maxScrollPos = this._maxScroll;
            if (currentScroll >= maxScrollPos - 1) {
                // Jump to the equivalent real slide first, then go next
                const currentIdx = this._getClosestSlideIndex(currentScroll);
                const numClones = this._numClones;
                const originalCount = slides.length - (numClones * 2);
                this._jumpToSlide(currentIdx - originalCount);
                // After jump, we need to recalculate nextIndex
                requestAnimationFrame(() => this.next());
                return true;
            }
        }

        for (let i = 0; i < slides.length; i++) {
            if (this._slideStart(slides[i]) > currentScroll + 1) {
                nextIndex = i;
                break;
            }
        }
        
        if (!this._isInfinite) {
            nextIndex = Math.min(nextIndex, Math.ceil(maxIndex));
        }
        this._scrollToIndexRaw(nextIndex);
        return true;
      }

      prev() {
        this._resetAutoplay(2);
        const currentScroll = this._scrollPos;
        const slides = this._getSlides();

        if (this._isInfinite && currentScroll <= 0) {
            // Jump to the equivalent real slide first, then go prev
            const currentIdx = this._getClosestSlideIndex(currentScroll);
            const numClones = this._numClones;
            const originalCount = slides.length - (numClones * 2);
            this._jumpToSlide(currentIdx + originalCount);
            requestAnimationFrame(() => this.prev());
            return true;
        }

        let prevIndex = 0;
        for (let i = slides.length - 1; i >= 0; i--) {
            if (this._slideStart(slides[i]) < currentScroll - 1) {
                prevIndex = i;
                break;
            }
        }
        
        if (!this._isInfinite) {
            prevIndex = Math.max(0, prevIndex);
        }
        this._scrollToIndexRaw(prevIndex);
        return true;
      }

      canNext() {
        if (this._isInfinite) return true;
        const slides = this._getSlides();
        const slidesPerView = parseFloat(getComputedStyle(this).getPropertyValue('--slider-slides-per-view')) || 1;
        const limit = Math.max(0, slides.length - slidesPerView);
        return this.state.currentIndex < limit;
      }

      canPrev() {
        if (this._isInfinite) return true;
        return this.state.currentIndex > 0;
      }

      scrollToIndex(index, behavior) {
        this._resetAutoplay(2);
        const isProgrammatic = typeof index === 'number';
        let targetIndex = isProgrammatic ? index : this.state.currentIndex;

        if (this._isInfinite) {
            // Map original index to the one in the middle section
            targetIndex = targetIndex + this._numClones;
        }

        this._scrollToIndexRaw(targetIndex, behavior);
      }

      // Re-evaluates the slide set after slides were added, removed, hidden or
      // shown (e.g. variant media filtering). Rebuilds infinite clones, recalculates
      // heights and re-aligns the scroll position without animation.
      refresh(index) {
        if (this._isInfinite) {
          this._isInfinite = false;
          this._cleanupInfinite();
        }
        this._checkInfinite();
        this._checkAspectRatio();

        const slides = this._getSlides();
        if (slides.length === 0) return;

        let targetIndex = typeof index === 'number' ? index : this.state.currentIndex;
        targetIndex = Math.max(0, Math.min(targetIndex, slides.length - 1));
        this.state.currentIndex = targetIndex;

        requestAnimationFrame(() => {
          this.scrollToIndex(targetIndex, 'instant');
        });
      }

      _scrollToIndexRaw(index, behavior = 'smooth') {
        const slides = this._getSlides();
        let targetIndex = index;
        
        // Safety clamp if not infinite
        const slidesPerView = parseFloat(getComputedStyle(this).getPropertyValue('--slider-slides-per-view')) || 1;
        const maxIndex = Math.max(0, slides.length - slidesPerView);
        
        if (!this._isInfinite) {
            targetIndex = Math.max(0, Math.min(targetIndex, Math.ceil(maxIndex)));
        } else {
            // For infinite, we just clamp to actual slide range to avoid errors
            targetIndex = Math.max(0, Math.min(targetIndex, slides.length - 1));
        }

        const targetSlide = slides[targetIndex];
        if (!targetSlide) return;

        this._isProgrammaticScroll = true;

        const maxScrollPos = this._maxScroll;
        const targetPos = Math.min(this._slideStart(targetSlide), maxScrollPos);

        // If we are already at the target, we should still clear the flag after a bit
        if (Math.abs(this._scrollPos - targetPos) < 1) {
            this.updateCurrentIndex();
            setTimeout(() => {
                this._isProgrammaticScroll = false;
            }, 50);
            return;
        }

        this.scrollTo(
          this._isVertical
            ? { top: targetPos, left: 0, behavior }
            : { top: 0, left: targetPos, behavior }
        );
      }
    }

    class PmdSliderNav extends HTMLElement {
      connectedCallback() {
        this._handleClick = () => {
          const direction = this.getAttribute('data-direction');
          const sliderId = this.getAttribute('data-slider');
          const slider = document.getElementById(sliderId);
          
          if (!slider) return;

          // Check if we can navigate before executing
          if (direction === 'next' && !slider.canNext()) return;
          if (direction === 'prev' && !slider.canPrev()) return;

          if (direction === 'next' && slider.next) {
            slider.next();
          } else if (direction === 'prev' && slider.prev) {
            slider.prev();
          }
          
          // Update our own state immediately
          this.updateDisabledState();
        };
        this.addEventListener('click', this._handleClick);

        // Update state when slider scrolls
        this._handleSliderScroll = (e) => {
          if (e.detail.sliderId === this.getAttribute('data-slider')) {
            this.updateDisabledState();
          }
        };
        window.addEventListener('pmd:slider:scroll', this._handleSliderScroll);
        
        // Initial state
        setTimeout(() => this.updateDisabledState(), 0);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._handleClick);
        window.removeEventListener('pmd:slider:scroll', this._handleSliderScroll);
      }

      updateDisabledState() {
        const direction = this.getAttribute('data-direction');
        const sliderId = this.getAttribute('data-slider');
        const slider = document.getElementById(sliderId);
        const button = this.querySelector('button');
        
        if (!slider || !button) return;

        let isDisabled = false;
        if (direction === 'next') {
          isDisabled = !slider.canNext();
        } else if (direction === 'prev') {
          isDisabled = !slider.canPrev();
        }

        if (isDisabled) {
          button.setAttribute('disabled', '');
        } else {
          button.removeAttribute('disabled');
        }
      }
    }

    class PmdSliderPagination extends HTMLElement {
      constructor() {
        super();
        this._dots = [];
        this._sliderId = null;
        this._slider = null;
      }

      connectedCallback() {
        this._sliderId = this.getAttribute('data-slider');
        this._init();
        
        this._handleSliderScroll = (e) => {
          if (e.detail.sliderId === this._sliderId) {
            this.updateActiveDot(e.detail.currentIndex);
          }
        };
        window.addEventListener('pmd:slider:scroll', this._handleSliderScroll);

        // Watch for slide changes (e.g. dynamic content or variant media filtering)
        this._observer = new MutationObserver(() => this._init());
        const slider = document.getElementById(this._sliderId);
        if (slider) {
          this._observer.observe(slider, { childList: true, subtree: true, attributeFilter: ['hidden'] });
        }
      }

      disconnectedCallback() {
        window.removeEventListener('pmd:slider:scroll', this._handleSliderScroll);
        if (this._observer) {
          this._observer.disconnect();
        }
      }

      _init() {
        this._slider = document.getElementById(this._sliderId);
        if (!this._slider) return;

        // Wait for slider to be ready or just use requestAnimationFrame
        requestAnimationFrame(() => {
          const slides = Array.from(this._slider.querySelectorAll('pmd-slide:not([cloned]):not([hidden])'));
          this.renderDots(slides.length);
          this.updateActiveDot(this._slider.state.currentIndex);
        });
      }

      renderDots(count) {
        this.innerHTML = '';
        this._dots = [];
        
        for (let i = 0; i < count; i++) {
          const dot = document.createElement('button');
          dot.classList.add('pmd-pagination-dot');
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => {
            if (this._slider) {
              this._slider.scrollToIndex(i);
            }
          });
          this.appendChild(dot);
          this._dots.push(dot);
        }
      }

      updateActiveDot(index) {
        this._dots.forEach((dot, i) => {
          if (i === index) {
            dot.classList.add('is-active');
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.classList.remove('is-active');
            dot.removeAttribute('aria-current');
          }
        });
      }
    }

    class PmdSliderProgressBar extends HTMLElement {
      constructor() {
        super();
        this._sliderId = null;
        this._slider = null;
        this._progressBar = null;
      }

      connectedCallback() {
        this._sliderId = this.getAttribute('data-slider');
        this._init();

        this._handleSliderScroll = (e) => {
          if (e.detail.sliderId === this._sliderId) {
            this._updateProgress();
          }
        };
        window.addEventListener('pmd:slider:scroll', this._handleSliderScroll);

        // Watch for slider changes
        this._observer = new MutationObserver(() => this._init());
        const slider = document.getElementById(this._sliderId);
        if (slider) {
          this._observer.observe(slider, { childList: true });
        }
      }

      disconnectedCallback() {
        window.removeEventListener('pmd:slider:scroll', this._handleSliderScroll);
        if (this._observer) {
          this._observer.disconnect();
        }
      }

      _init() {
        this._slider = document.getElementById(this._sliderId);
        if (!this._slider) return;

        this.innerHTML = '';
        this._progressBar = document.createElement('div');
        this._progressBar.classList.add('pmd-slider-progress-bar-inner');
        this.appendChild(this._progressBar);

        requestAnimationFrame(() => {
          this._updateProgress();
        });
      }

      _updateProgress() {
        if (!this._slider || !this._progressBar) return;

        // Track whichever axis the slider scrolls on. pmd-slider mirrors
        // --slider-direction onto a [direction] attribute.
        const isVertical = this._slider.getAttribute('direction') === 'vertical';
        // Mirror the axis so the bar's own CSS can lay itself out vertically.
        this.setAttribute('direction', isVertical ? 'vertical' : 'horizontal');
        const scrollPos = isVertical ? this._slider.scrollTop : this._slider.scrollLeft;
        const scrollSize = isVertical ? this._slider.scrollHeight : this._slider.scrollWidth;
        const clientSize = isVertical ? this._slider.clientHeight : this._slider.clientWidth;
        const maxScroll = scrollSize - clientSize;

        // The fill grows along the scroll axis; the cross axis is left to CSS.
        const fillProp = isVertical ? 'height' : 'width';
        const crossProp = isVertical ? 'width' : 'height';
        this._progressBar.style.removeProperty(crossProp);

        if (maxScroll <= 0) {
          this._progressBar.style[fillProp] = '0%';
          return;
        }

        const progress = (scrollPos / maxScroll) * 100;
        this._progressBar.style[fillProp] = `${Math.min(100, Math.max(0, progress))}%`;
      }
    }

    class PmdSliderThumbs extends HTMLElement {
      constructor() {
        super();
        this.state = {
          currentIndex: 0
        };
      }

      // Keep index math aligned with the main slider: [hidden] thumbs are excluded.
      _getSlides() {
        return Array.from(this.querySelectorAll('pmd-slide:not([hidden])'));
      }

      // --- Axis abstraction ---------------------------------------------------
      // Mirrors pmd-slider: --slider-direction drives which axis the thumb strip
      // scrolls on, mirrored onto a [direction] attribute so CSS can select it.
      get _isVertical() {
        return this.getAttribute('direction') === 'vertical';
      }

      get _scrollPos() {
        return this._isVertical ? this.scrollTop : this.scrollLeft;
      }

      set _scrollPos(value) {
        if (this._isVertical) this.scrollTop = value;
        else this.scrollLeft = value;
      }

      get _maxScroll() {
        return this._isVertical
          ? this.scrollHeight - this.clientHeight
          : this.scrollWidth - this.clientWidth;
      }

      _slideStart(slide) {
        return this._isVertical ? slide.offsetTop : slide.offsetLeft;
      }

      _slideSize(slide) {
        return this._isVertical ? slide.offsetHeight : slide.offsetWidth;
      }

      _pointerPos(e) {
        return this._isVertical ? e.pageY : e.pageX;
      }

      _checkDirection() {
        const isVertical = getComputedStyle(this)
          .getPropertyValue('--slider-direction').trim() === 'vertical';
        this.setAttribute('direction', isVertical ? 'vertical' : 'horizontal');
      }

      connectedCallback() {
        this._checkDirection();
        this._checkDraggable();

        // Maintain current slide position on resize
        this._onResize = () => {
          this._checkDirection();
          this._checkDraggable();
          this.scrollToIndex(); 
        };
        window.addEventListener('resize', this._onResize);

        this._resizeObserver = new ResizeObserver(() => this._onResize());
        this._resizeObserver.observe(this);

        this.setupThumbClicks();

        // Mouse events for freedrag
        this._onMouseDown = (e) => {
          if (this.getAttribute('draggable-enabled') !== 'true') return;
          this.handleStart(this._pointerPos(e));
          this.classList.add('is-dragging');
        };
        this._onMouseMove = (e) => {
          if (!this._isDragging) return;
          e.preventDefault();
          this.handleMove(this._pointerPos(e));
        };
        this._onMouseUp = () => {
          if (!this._isDragging) return;
          this.handleEnd();
          this.classList.remove('is-dragging');
        };

        this.addEventListener('mousedown', this._onMouseDown);
        // Native HTML5 drag on images/links hijacks the pointer before our drag
        // logic sees it. Firefox ignores -webkit-user-drag, so cancel it here too.
        this._onDragStart = (e) => {
          if (this.getAttribute('draggable-enabled') !== 'true') return;
          e.preventDefault();
        };
        this.addEventListener('dragstart', this._onDragStart);
        window.addEventListener('mousemove', this._onMouseMove);
        window.addEventListener('mouseup', this._onMouseUp);
        
        // Listen for main slider changes to sync thumbs
        this._handleMainSliderScroll = (e) => {
          if (e.detail.sliderId === this.getAttribute('data-slider')) {
            this.scrollToIndex(e.detail.currentIndex);
          }
        };
        this.updateActiveThumb();
        window.addEventListener('pmd:slider:scroll', this._handleMainSliderScroll);
      }

      _checkDraggable() {
        // Check if the primary pointer is coarse (like a finger)
        const isTouchPrimary = window.matchMedia("(pointer: coarse)").matches;
        // For thumbs, we enable dragging on desktop (non-touch) by default or we can use a property
        // The issue description says "add freedrag for thumbs on desktop (devices that dont support touch)"
        const isDraggable = !isTouchPrimary;
        this.setAttribute('draggable-enabled', isDraggable);
      }

      handleStart(pointerPos) {
        this._isDragging = true;
        this._startPointer = pointerPos;
        this._startScrollPos = this._scrollPos;
        
        this.style.scrollBehavior = 'auto';
        this._isProgrammaticScroll = false;
      }

      handleMove(pointerPos) {
        if (!this._isDragging) return;
        const walk = (pointerPos - this._startPointer);
        this._scrollPos = this._startScrollPos - walk;
      }

      handleEnd() {
        if (!this._isDragging) return;
        this._isDragging = false;
        this.style.scrollBehavior = ''; // Restore original behavior
        
        // Check if it was a drag to prevent the next click if it happens on a slide
        const distance = Math.abs(this._scrollPos - this._startScrollPos);
        if (distance > 5) {
          this._wasDragging = true;
          setTimeout(() => this._wasDragging = false, 100);
        }
      }

      disconnectedCallback() {
        window.removeEventListener('pmd:slider:scroll', this._handleMainSliderScroll);
        window.removeEventListener('resize', this._onResize);
        this.removeEventListener('mousedown', this._onMouseDown);
        this.removeEventListener('dragstart', this._onDragStart);
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mouseup', this._onMouseUp);
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
        }
      }

      setupThumbClicks() {
        this.addEventListener('click', (e) => {
          if (this._wasDragging) {
            e.preventDefault();
            return;
          }
          const slide = e.target.closest('pmd-slide');
          if (slide) {
            const slides = this._getSlides();
            const index = slides.indexOf(slide);
            if (index === -1) return;
            const sliderId = this.getAttribute('data-slider');
            
            if (sliderId) {
              const mainSlider = document.getElementById(sliderId);
              if (mainSlider && mainSlider.scrollToIndex) {
                this.scrollToIndex(index);
                mainSlider.scrollToIndex(index);
              }
            }
          }
        });
      }

      updateActiveThumb() {
        const slides = this._getSlides();
        slides.forEach((slide, index) => {
          slide.classList.remove('is-active');
          if (index === this.state.currentIndex) {
            slide.classList.add('is-active');
          }
        });
      }

      scrollToIndex(index) {
        const slides = this._getSlides();
        const isProgrammatic = typeof index === 'number';
        const targetIndex = isProgrammatic ? index : this.state.currentIndex;
        const targetSlide = slides[targetIndex];
        
        if (!targetSlide) return;

        const containerRect = this.getBoundingClientRect();
        const slideRect = targetSlide.getBoundingClientRect();
        
        // Check if target slide is fully visible
        const isVisible = (
          slideRect.left >= containerRect.left - 1 && 
          slideRect.right <= containerRect.right + 1
        );

        if (!isVisible) {
          // Find all fully visible slides
          const visibleSlides = slides.filter(slide => {
            const rect = slide.getBoundingClientRect();
            return rect.left >= containerRect.left - 1 && rect.right <= containerRect.right + 1;
          });

          let targetPos;

          if (visibleSlides.length > 0) {
            // Find the range of slides to center
            const firstVisibleIdx = slides.indexOf(visibleSlides[0]);
            const lastVisibleIdx = slides.indexOf(visibleSlides[visibleSlides.length - 1]);
            const targetIdx = slides.indexOf(targetSlide);
            
            let newFirstIdx, newLastIdx;

            if (targetIdx > lastVisibleIdx) {
              // Moving forward: target becomes the last visible
              newLastIdx = targetIdx;
              newFirstIdx = Math.max(0, targetIdx - (visibleSlides.length - 1));
            } else if (targetIdx < firstVisibleIdx) {
              // Moving backward: target becomes the first visible
              newFirstIdx = targetIdx;
              newLastIdx = Math.min(slides.length - 1, targetIdx + (visibleSlides.length - 1));
            } else {
              // Already visible, shouldn't happen due to !isVisible check but for safety:
              newFirstIdx = firstVisibleIdx;
              newLastIdx = lastVisibleIdx;
            }

            const firstSlide = slides[newFirstIdx];
            const lastSlide = slides[newLastIdx];
            
            const rangeSize = (this._slideStart(lastSlide) + this._slideSize(lastSlide)) - this._slideStart(firstSlide);
            const rangeCenter = this._slideStart(firstSlide) + (rangeSize / 2);
            const containerSize = this._isVertical ? containerRect.height : containerRect.width;
            
            targetPos = rangeCenter - (containerSize / 2);
          } else {
            // Fallback to centering the target slide if nothing is fully visible
            const containerSize = this._isVertical ? containerRect.height : containerRect.width;
            targetPos = this._slideStart(targetSlide) - (containerSize / 2) + (this._slideSize(targetSlide) / 2);
          }

          targetPos = Math.max(0, Math.min(targetPos, this._maxScroll));

          this.scrollTo(
            this._isVertical
              ? { top: targetPos, left: 0, behavior: 'smooth' }
              : { top: 0, left: targetPos, behavior: 'smooth' }
          );
        }

        this.state.currentIndex = targetIndex;
        this.updateActiveThumb();
      }
    }

    customElements.define('pmd-slide', PmdSlide);
    customElements.define('pmd-slide-content', PmdSlideContent);
    customElements.define('pmd-slider', PmdSlider);
    customElements.define('pmd-slider-nav', PmdSliderNav);
    customElements.define('pmd-slider-pagination', PmdSliderPagination);
    customElements.define('pmd-slider-progress-bar', PmdSliderProgressBar);
    customElements.define('pmd-slider-thumbs', PmdSliderThumbs);

    class PmdDesktopContainer extends HTMLElement {
      constructor() {
        super();
        this._targets = [];
        this._isDesktop = false;
        this._onResize = this._onResize.bind(this);
      }

      static get observedAttributes() {
        return ['breakpoint', 'target-class'];
      }

      connectedCallback() {
        this._init();
        window.addEventListener('resize', this._onResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._onResize);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue && this.isConnected) {
          this._init();
        }
      }

      _init() {
        const targetClass = this.getAttribute('target-class');
        if (!targetClass) return;

        // Collect targets and their original positions if not already collected
        const elements = document.querySelectorAll(`.${targetClass}`);
        this._targets = Array.from(elements).map(el => {
          const placeholder = document.createComment(`pmd-placeholder-${targetClass}`);
          return {
            element: el,
            placeholder: placeholder
          };
        });

        this._checkBreakpoint();
      }

      _onResize() {
        this._checkBreakpoint();
      }

      _checkBreakpoint() {
        const breakpoint = parseInt(this.getAttribute('breakpoint')) || 1024;
        const isNowDesktop = window.innerWidth >= breakpoint;

        if (isNowDesktop !== this._isDesktop) {
          this._isDesktop = isNowDesktop;
          this._moveElements();
        }
      }

      _moveElements() {
        if (this._isDesktop) {
          this._targets.forEach(target => {
            // Replace element with placeholder in its original position
            if (target.element.parentElement) {
              target.element.parentElement.replaceChild(target.placeholder, target.element);
            }
            // Move element to this container
            this.appendChild(target.element);
          });
        } else {
          this._targets.forEach(target => {
            // Put element back where placeholder is
            if (target.placeholder.parentElement) {
              target.placeholder.parentElement.replaceChild(target.element, target.placeholder);
            }
          });
        }
      }
    }

    if (!customElements.get('pmd-desktop-container')) {
      customElements.define('pmd-desktop-container', PmdDesktopContainer);
    }

    exports.PMDATCButton = PMDATCButton;
    exports.PMDFinalPrice = PMDFinalPrice;
    exports.PMDOneTimePurchase = PMDOneTimePurchase;
    exports.PMDOptionValue = PMDOptionValue;
    exports.PMDPriceCompareAt = PMDPriceCompareAt;
    exports.PMDPriceRegular = PMDPriceRegular;
    exports.PMDProduct = PMDProduct;
    exports.PMDProductOption = PMDProductOption;
    exports.PMDSelectContainer = PMDSelectContainer;
    exports.PMDSelectIcon = PMDSelectIcon;
    exports.PMDSubscriptionBox = PMDSubscriptionBox;
    exports.PMDSubscriptionGroup = PMDSubscriptionGroup;
    exports.PMDSubscriptionGroupDiscount = PMDSubscriptionGroupDiscount;
    exports.PMDSubscriptionGroupPrice = PMDSubscriptionGroupPrice;
    exports.PMDVariantMedia = PMDVariantMedia;
    exports.PMDVariantTitle = PMDVariantTitle;
    exports.PmdDesktopContainer = PmdDesktopContainer;
    exports.PmdSlide = PmdSlide;
    exports.PmdSlideContent = PmdSlideContent;
    exports.PmdSlider = PmdSlider;
    exports.PmdSliderNav = PmdSliderNav;
    exports.PmdSliderPagination = PmdSliderPagination;
    exports.PmdSliderProgressBar = PmdSliderProgressBar;
    exports.PmdSliderThumbs = PmdSliderThumbs;

    return exports;

})({});
//# sourceMappingURL=index.bundle.js.map
